import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { withRouter } from 'react-router-dom';
import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import dataLayer from '../../../../../utils/dataLayer';

import Tags, { getTag } from '../../../../../components/common/Tags';
import { AppliedCouponTag } from '../../../../../components/common/Tags/defaultTags';

import {
  openModalQuotationDateWarning,
  addComparisonPlan,
  removeComparisonPlan,
  openModalUpsell,
  setUpsellPlans,
} from '../../../../../actions/searchActions';
import { fetchUpsellPlan } from '../../../../../apiCalls';
import {
  getProductPlanUrl,
  getProductPurchaseUrl,
  sendToRDStation,
  insertTagPurchaseUrl,
  selectPlanAndRedirect,
  hasBenefitCode,
} from '../../../../../utils/plan';
import { maxValueParcel } from '../../../../../utils/plan';

import { formatPrice, getProductDiscount } from '../../../../../utils';

import PixDiscountTag from '../../../../../components/search/plans_table/PixDiscountTag';
import HoverTooltip from '../../../common/Tooltip';
import { TooltipIcon } from '../../../../../assets/icons/Tooltip.icon';
import { SalaVipIcon } from '../../../../../assets/icons/SalaVip.icon';
import { ChipInternacionalIcon } from '../../../../../assets/icons/ChipInternacional.icon';
import styles from './styles.module.css';

const codeTagSpecialistIndicator = '70';
const codeTagPromotionalDestination = '83';
const benefitCodeSalaVip = '68';
const benefitCodeChipInternacional = '69';

const Plan = ({
  match,
  plan,
  paymentMethods,
  showSpecialistSuggestions,
  mobile,
  providers,
  coupon,
  addComparisonPlan,
  removeComparisonPlan,
  openModalUpsell,
  setUpsellPlans,
  comparisonPlans = [],
  destinations,
}) => {
  const upsellPlanRef = useRef(null);
  const upsellFetchedRef = useRef(false);
  const hoverTimerRef = useRef(null);

  const handleMouseEnter = useCallback(() => {
    hoverTimerRef.current = setTimeout(function () {
      dataLayer.viewItem(plan);
    }, 500);
  }, [plan]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }
  }, []);

  const productUrl = useMemo(() => getProductPlanUrl(match, plan.code), [match, plan.code]);
  const productPurchaseUrl = useMemo(() => getProductPurchaseUrl(match, plan.code), [match, plan.code]);

  const pixDiscountMultiplier = useMemo(() => {
    if (paymentMethods && paymentMethods.length > 0) {
      const pixPayment = paymentMethods.find((method) => method.slug === 'pix');
      if (pixPayment && pixPayment.discount > 0) {
        return (100 - pixPayment.discount) / 100;
      }
    }
    return 0.95;
  }, [paymentMethods]);

  const basePrice = plan.adult.price;
  const priceWithCouponDiscount = useMemo(
    () => getProductDiscount(basePrice, plan.code, coupon),
    [coupon, basePrice, plan.code]
  );
  const finalPrice = useMemo(
    () => priceWithCouponDiscount || plan.price_with_coupon_discount || basePrice,
    [priceWithCouponDiscount, plan.price_with_coupon_discount, basePrice]
  );
  const parcels = useMemo(() => maxValueParcel(finalPrice) || 1, [finalPrice]);

  const selectPlan = useCallback(() => {
    sendToRDStation();
    const tags = [];

    if (plan.extra.specialist_indicator && showSpecialistSuggestions) {
      tags.push(codeTagSpecialistIndicator);
    }

    const foundDestination = destinations.find((destination) => destination.slug === match.params.destination);
    if (foundDestination && foundDestination.promotional) {
      tags.push(codeTagPromotionalDestination);
    }

    selectPlanAndRedirect(insertTagPurchaseUrl(productPurchaseUrl, tags));
  }, [plan, match, destinations, showSpecialistSuggestions, productPurchaseUrl, selectPlanAndRedirect, insertTagPurchaseUrl]);

  useEffect(() => {
    if (plan.upsell_product_id == null || upsellFetchedRef.current) return;

    upsellFetchedRef.current = true;
    const { destination, begin_date, end_date } = match.params;

    fetchUpsellPlan({
      begin_date,
      end_date,
      destination,
      product_code: plan.upsell_product_id,
    }).then((data) => {
      upsellPlanRef.current = data;
    });
  }, [plan.upsell_product_id, match.params]);

  const checkUpsellIsAvailable = useCallback(async () => {
    if (plan.upsell_product_id != null) {
      let upsellPlanData = upsellPlanRef.current;

      if (!upsellPlanData) {
        const { destination, begin_date, end_date } = match.params;
        upsellPlanData = await fetchUpsellPlan({
          begin_date,
          end_date,
          destination,
          product_code: plan.upsell_product_id,
        });
      }

      if (upsellPlanData && upsellPlanData.code) {
        setUpsellPlans([plan, upsellPlanData]);
        return openModalUpsell();
      }
    }

    return selectPlan();
  }, [plan, match.params, setUpsellPlans, openModalUpsell]);

  const provider = useMemo(
    () => providers.find((provider) => provider.code === plan.provider_code),
    [providers, plan.provider_code]
  );
  const providerImgPath = provider ? `/assets/images/seguradoras/${provider.slug}.png` : '';

  const benefits = useMemo(() => {
    const baggage = plan.benefits.find((benefit) => benefit.code === '42' || benefit.code === 42);
    const hospital = plan.benefits.find((benefit) => benefit.code === '51' || benefit.code === 51);
    const medicament = plan.benefits.find((benefit) => benefit.code === '5' || benefit.code === 5);
    const telemedicine = plan.benefits.find((benefit) => benefit.code === '56' || benefit.code === 56);
    return { baggage, hospital, medicament, telemedicine };
  }, [plan.benefits]);

  const hasSalaVip = useMemo(() => hasBenefitCode(plan, benefitCodeSalaVip), [plan.benefits]);
  const hasChipInternacional = useMemo(() => hasBenefitCode(plan, benefitCodeChipInternacional), [plan.benefits]);

  const hasSingleHighlight = hasSalaVip !== hasChipInternacional;

  const tag = getTag(plan);

  const isSpecialist = plan.extra.specialist_indicator && showSpecialistSuggestions;

  const isSelected = useMemo(
    () => (comparisonPlans || []).some((p) => p && String(p.code) === String(plan.code)),
    [comparisonPlans, plan.code]
  );

  const formatBenefitsCoverage = (benefitCoverage) => {
    if (!benefitCoverage || typeof benefitCoverage !== 'string') return 'Não';
    const match = benefitCoverage.match(/[\d.,]+/);
    if (!match || !match[0]) return 'Não';
    return `$ ${match[0]}`;
  };

  return (
    <div
      className={
        styles.planGrid + (isSpecialist ? ` ${styles.specialistPlan}` : '') + (isSelected ? ` ${styles.selected}` : '')
      }
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={`${styles.specialistBox} ${isSpecialist ? '' : styles.hide}`}>
        <span role="img" aria-label="Recomendação do especialista">
          👨‍💻
        </span>{' '}
        Recomendação do especialista
      </div>
      <div className={`${styles.triangleTopleft} ${isSelected ? '' : styles.hide}`}>
        <img className={styles.selecionadoIco} src="/assets/images/selecionado-ico.svg" alt="Selecionado" />
      </div>

      {!mobile && (
        <div className={styles.planHeader}>
          <div className={styles.col1}>Plano e benefícios</div>
          <div className={styles.col2}>Total por segurado</div>
        </div>
      )}
      <div className={styles.planContent}>
        <div className={styles.col1}>
          <div className={styles.planInfo}>
            <Tags tag={tag} isMobile />
            <Tags tag={tag} />

            <div className={styles.planNameContainer}>
              <div className={styles.planNameRow}>
                <div className={styles.image}>
                  <img src={providerImgPath} alt="" />
                </div>

                <div className={styles.planName}>
                  <strong>{plan.name}</strong>

                  {!mobile && (
                    <div className={styles.planDetails}>
                      <div className={styles.detailItem}>
                        <span>
                          Faixa etária: {plan.child.min_age} a {plan.elder.max_age} anos
                        </span>
                      </div>

                      {plan.elder.max_age !== plan.adult.max_age && (
                        <React.Fragment>
                          <div className={styles.planDetailsSeparator}></div>

                          <div className={styles.detailItem}>
                            <span>
                              Maiores de {plan.elder.min_age} anos: <strong>{`R$ ${formatPrice(plan.elder.price)}`}</strong>
                            </span>
                          </div>
                        </React.Fragment>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {mobile && (
                <div className={styles.planAgeDetails}>
                  <span>
                    Faixa etária: {plan.child.min_age} a {plan.adult.max_age} anos
                  </span>
                  {plan.elder.max_age !== plan.adult.max_age && (
                    <span>
                      Maiores de {plan.elder.min_age} anos: <strong>{`R$ ${formatPrice(plan.elder.price)}`}</strong>
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className={styles.benefits}>
              <div className={styles.group}>
                {hasSalaVip ? (
                  <div className={`${styles.highlightItem} ${hasSingleHighlight ? styles.highlightItemAligned : ''}`}>
                    <div className={styles.withTooltip}>
                      <div className={styles.highlightLabel}>
                        <SalaVipIcon className={styles.highlightIcon} />
                        <span>
                          Acesso a <strong>Sala VIP</strong>
                        </span>
                      </div>
                      <HoverTooltip content="Espere seu voo em salas VIP de aeroportos: poltronas, wi-fi, comida e bebida inclusos, sem precisar de cartão premium.">
                        <TooltipIcon color="#5A539C" />
                      </HoverTooltip>
                    </div>
                  </div>
                ) : (
                  <div className={styles.benefitItem}>
                    <div>Despesa Médica Hospitalar</div>
                    <strong>{(benefits.hospital && formatBenefitsCoverage(benefits.hospital.coverage)) || 'Não'}</strong>
                  </div>
                )}

                <div className={styles.benefitItem}>
                  <div>Reembolso de medicamentos</div>
                  <strong>
                    {(benefits.medicament && formatBenefitsCoverage(benefits.medicament.coverage)) || 'Não'}
                  </strong>
                </div>
              </div>

              <div className={styles.group}>
                {hasChipInternacional ? (
                  <div className={`${styles.highlightItem} ${hasSingleHighlight ? styles.highlightItemAligned : ''}`}>
                    <div className={styles.withTooltip}>
                      <div className={styles.highlightLabel}>
                        <ChipInternacionalIcon className={styles.highlightIcon} />
                        <span>
                          Inclui <strong>Chip Internacional</strong>
                        </span>
                      </div>
                      <HoverTooltip content="Chip com internet internacional para você usar durante toda a viagem.">
                        <TooltipIcon color="#5A539C" />
                      </HoverTooltip>
                    </div>
                  </div>
                ) : (
                  <div className={styles.benefitItem}>
                    <div>Seguro de Bagagem Extraviada</div>
                    <strong>{(benefits.baggage && formatBenefitsCoverage(benefits.baggage.coverage)) || 'Não'}</strong>
                  </div>
                )}
                <div className={styles.benefitItem}>
                  <div className={styles.withTooltip}>
                    Possui Telemedicina?{' '}
                    <HoverTooltip content="É a cobertura para consultas médicas realizadas à distância, por videochamada ou telefone, para orientação em casos de baixa complexidade.">
                      <TooltipIcon />
                    </HoverTooltip>
                  </div>
                  <strong>{(benefits.telemedicine && benefits.telemedicine.coverage) || 'Não'}</strong>
                </div>
              </div>
            </div>

            <div className={styles.link}>
              <Link to={productUrl} data-cy={`link-ver-cobertura-completa`}>
                Ver a cobertura completa
              </Link>
            </div>
          </div>
        </div>
        <div className={styles.col2}>
          <div className={styles.planInfoPrice}>
            {priceWithCouponDiscount > 0 && (
              <React.Fragment>
                <Tags tag={AppliedCouponTag} isMobile customClassName="search-applied-coupon" />
                <Tags tag={AppliedCouponTag} isMobile={false} customClassName="search-applied-coupon" />
              </React.Fragment>
            )}

            <div className={styles.priceContainer}>
              {priceWithCouponDiscount > 0 && <span className={styles.oldPrice}>R$ {formatPrice(basePrice)}</span>}
              <span className={styles.priceCreditCard}>
                <strong>R$ {formatPrice(finalPrice)}</strong> {parcels}x sem juros no cartão
              </span>
              <div className={styles.pricePix}>
                <span className={styles.row}>
                  <strong>R$ {formatPrice(finalPrice * pixDiscountMultiplier)}</strong>
                  <PixDiscountTag color="#292828" />
                </span>
                <span>/ por pessoa no PIX</span>
              </div>
            </div>

            <div className={styles.buttons}>
              <button
                className={styles.selectPlanButton}
                onClick={checkUpsellIsAvailable}
                data-cy={`btn-selecionar-plano`}
              >
                Selecionar plano
              </button>

              <button
                className={styles.comparisonButton}
                onClick={() => {
                  if (isSelected) {
                    removeComparisonPlan(plan.code);
                  } else {
                    addComparisonPlan({ ...plan });
                  }
                }}
                data-cy={`btn-comparar-plano`}
              >
                Comparar plano
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(
  connect(
    (state) => ({
      paymentMethods: state.purchase.paymentMethods,
      showSpecialistSuggestions: state.search.showSpecialistSuggestions,
      mobile: state.mobile.mobileActive,
      providers: state.global.providers,
      coupon: state.purchase.coupon,
      comparisonPlans: state.search.comparisonPlans,
      destinations: state.global.destinations,
    }),
    {
      openModalQuotationDateWarning,
      addComparisonPlan,
      removeComparisonPlan,
      openModalUpsell,
      setUpsellPlans,
    }
  )(Plan)
);



// WEBPACK FOOTER //
// ./src/views/v2/search/components/plan/index.jsx